/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedNumber } from 'js/lib/coursera.react-intl';

import { Typography2 as Typography, breakpoints } from '@coursera/cds-core';

const DEFAULT_CURRENCY_CODE = 'USD';
const VARIANTS = {
  BASE: 'base',
  CAREER: 'career',
} as const;

type Props = {
  roleTitle: string;
  salary?: number | null;
  currencyCode?: string | null;
  width: number;
  variant: (typeof VARIANTS)[keyof typeof VARIANTS];
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
    ${breakpoints.up('sm')} {
      flex-direction: row;
      gap: var(--cds-spacing-200);
      align-items: center;
    }
  `,
  roleTitleContainer: (width: number, isBase: boolean) => css`
    ${breakpoints.up('sm')} {
      background: ${isBase ? 'var(--cds-color-darken-975)' : 'var(--cds-color-blue-750)'};
      padding: var(--cds-spacing-75) var(--cds-spacing-100);
      border-radius: var(--cds-border-radius-50);
      width: ${width}%;
    }
  `,
  roleTitle: css`
    font-weight: 400;
    white-space: nowrap;
    color: var(--cds-color-neutral-primary-weak);
    ${breakpoints.up('sm')} {
      font-weight: 600;
      color: var(--cds-color-white-0);
    }
  `,
  salaryContainer: (width: number, isBase: boolean) => css`
    background: ${isBase ? 'var(--cds-color-darken-975)' : 'var(--cds-color-blue-750)'};
    width: ${width}%;
    padding: var(--cds-spacing-150) var(--cds-spacing-200);
    border-radius: var(--cds-border-radius-50);
    ${breakpoints.up('sm')} {
      font-weight: 600;
      width: auto;
      color: ${isBase ? 'var(--cds-color-darken-500)' : 'var(--cds-color-darken-0)'};
      background: transparent;
      padding: unset;
      border-radius: unset;
    }
  `,
  salary: css`
    color: var(--cds-color-darken-950);
    font-weight: 600;
    ${breakpoints.down('xs')} {
      color: var(--cds-color-white-0);
    }
  `,
};

const SalaryBar = ({ roleTitle, salary, currencyCode, width, variant }: Props) => {
  if (!roleTitle || !salary) {
    return null;
  }

  const isBase = variant === VARIANTS.BASE;

  return (
    <div css={styles.container}>
      <div css={styles.roleTitleContainer(width, isBase)}>
        <Typography variant="bodySecondary" component="div" css={styles.roleTitle}>
          {roleTitle}
        </Typography>
      </div>
      <div css={styles.salaryContainer(width, isBase)}>
        <Typography variant="bodyPrimary" component="div" css={styles.salary}>
          <FormattedNumber
            value={salary}
            currency={currencyCode ?? DEFAULT_CURRENCY_CODE}
            style="currency"
            maximumFractionDigits={0}
            minimumFractionDigits={0}
          />
        </Typography>
      </div>
    </div>
  );
};

export default SalaryBar;
